import React, { useEffect, useState } from 'react';
import {
  PieChartOutlined,
  UsergroupAddOutlined,
  ProductOutlined,
  SolutionOutlined,
  CarOutlined,
  EnvironmentOutlined,
  UserOutlined,
  InteractionOutlined,
  BankOutlined,
  BorderOuterOutlined,
  FileTextOutlined,
  DashboardOutlined,
  ClusterOutlined,
  CodepenOutlined,
  AimOutlined,
} from '@ant-design/icons';
import { Layout, Menu, theme } from 'antd';
import PageHeader from '../../../src/components/common/Header';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

const { Content, Sider } = Layout;
const contentStyle = {
  background: '#e9f0ee',
  minHeight: 'calc(100vh - 54px)',
};

const DefaultLayout = () => {
  
  const isScreenSmall = () => window.innerWidth <= 1024;

  const [isMobileView, setIsMobileView] = useState(isScreenSmall());
  const [collapsed, setCollapsed] = useState(false);
  const [openKeys, setOpenKeys] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState([]);

  const location = useLocation();

  const getPath = (pathname) => {
    const paths = pathname?.split('/')?.filter(Boolean);
    if (paths.includes('masters') || paths?.includes('reports')) {
      return `/${paths[0]}/${paths[1]}/${paths[2]}`;
    }
    if (paths.includes('admin') && paths.includes('leads')) {
      return `/${paths[0]}/${paths[1]}`;
    }
    if (paths.includes('admin') && paths.includes('stock')) {
      return `/${paths[0]}/${paths[1]}`;
    }
    if (paths.includes('admin') && paths.includes('requests')) {
      return pathname; // Return full path for requests routes
    }
    if (paths.includes('admin')) {
      return `/${paths[0]}/${paths[1]}`;
    }
    if (!paths.includes('admin')) {
      return paths[0];
    }
    return pathname;
  };

  useEffect(() => {
    const path = getPath(location.pathname);
    const paths = path?.split('/')?.filter(Boolean);
    const basePath = paths?.length >= 2 ? `/${paths[0]}/${paths[1]}` : path;
    const searchParams = new URLSearchParams(location.search);
    const isStockDashboardRedirect =
      searchParams.get('stock_dashboard_redirect') === 'true';

    // Handle query parameters for dynamic menu items
    const fullPath = location.pathname + location.search;
    const isExchangeListRoute =
      location.pathname.includes('/admin/leads') ||
      location.pathname.includes('/leads/exchange-lead') ||
      location.pathname.includes('/leads');
    const isStockListRoute = location.pathname.includes('/admin/stock');

    let selectedKey;
    if (isStockDashboardRedirect) {
      setSelectedKeys([]);
      setOpenKeys([]);
      return;
    } else if (isExchangeListRoute) {
      selectedKey = fullPath.includes('?') ? fullPath : location.pathname;
    } else if (
      fullPath.includes('lead_exchange_stage_id') ||
      fullPath.includes('approval_pending_for_final_inspection') ||
      fullPath.includes('un_allocated')
    ) {
      selectedKey = fullPath;
    } else if (isStockListRoute) {
      selectedKey = '/admin/stock';
    } else {
      selectedKey = path;
    }

    setSelectedKeys([selectedKey]);

    if (isExchangeListRoute) {
      setOpenKeys(['leads']);
    } else if (location.pathname.includes('/admin/requests')) {
      setOpenKeys(['/admin/requests']);
    } else if (isStockListRoute) {
      setOpenKeys([]);
    } else {
      setOpenKeys([basePath]);
    }
  }, [location.pathname, location.search]);

  const handleMainContentClick = () => {
    // Only close menu on mobile devices
    if (isScreenSmall()) {
      setIsMobileView(true);
    }
  };


  const onOpenChange = (keys) => {
    setOpenKeys(keys);
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(isScreenSmall());
      setCollapsed(false);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);



  const {
    token: { colorBgContainer },
  } = theme.useToken();

  const isAdmin = useSelector((state) => state?.auth?.isAdmin);
  const roleType = useSelector((state) => state?.auth?.userData?.role);
  const permissions = useSelector((state) => state?.auth?.userData?.permissions);
  
  // Check if user can see Pending Requests (not Exchange Evaluation Team or Procurement Manager)
  const canSeePendingRequests = 
    roleType !== 'Exchange Evaluation Team' && 
    roleType !== 'Procurement Manager';
  // Helper function to check if user should see dynamic stages menu
  // const shouldShowDynamicStagesMenu = () => {
  //   const salesTeamRoles = [
  //     'Super Admin', 'CEO', 'VP Sales', 'GM (General Manager)', 
  //     'AGM (Assistant General Manager)', 'TSM (Territory Sales Manager)', 
  //     'Sales Manager', 'Team Leader', 'Sales Officer'
  //   ];
  //   const result = roleType === 'Exchange Evaluation Team' || 
  //          roleType === 'Procurement Manager' || 
  //          salesTeamRoles.includes(roleType);
  //   return result;
  // };

  // Helper function to get the base leads URL based on role
  const getLeadsBaseUrl = () => {
    if (roleType === 'Super Admin') {
      return '/admin/leads';
    } else if (roleType === 'Exchange Evaluation Team' || roleType === 'Procurement Manager') {
      return '/leads/exchange-lead';
    } else {
      return '/leads';
    }
  };

  // Helper function to check if user should see Unallocated option
  // const shouldShowUnallocated = () => {
  //   const salesTeamRoles = [
  //     'Super Admin', 'CEO', 'VP Sales', 'GM (General Manager)', 
  //     'AGM (Assistant General Manager)', 'TSM (Territory Sales Manager)', 
  //     'Sales Manager', 'Team Leader', 'Sales Officer'
  //   ];
  //   const result = salesTeamRoles.includes(roleType);
  //   return result;
  // };

  // Exchange stages are now fetched in Leads components, not needed here for menu

  const hasLeadPermission =
    // permissions?.includes("add-lead") ||
    permissions?.includes('view-lead');
  // permissions?.includes("edit-lead") ||
  // permissions?.includes("delete-lead");

  // Exchange Evaluation Team and Procurement Manager should always see Leads menu
  const shouldShowLeadsMenu = hasLeadPermission ||
    roleType === 'Exchange Evaluation Team' ||
    roleType === 'Procurement Manager';

  const hasUserPermission =
    // permissions?.includes("add-user") ||
    permissions?.includes('view-user');
  // permissions?.includes("edit-user") ||
  // permissions?.includes("delete-user");

  const hasTargetPermission = permissions?.includes('add-target');
  // permissions?.includes("view-target")||;
  // permissions?.includes("edit-target") ||
  // permissions?.includes("delete-target");

  const hasStockPermission = permissions?.includes('view-new-vehicle-stock');
  const hasRequestPermission = permissions?.includes('view-request');

  const adminSideBarOptions = [
    {
      key: '/admin/dashboard',
      icon: <PieChartOutlined />,
      label: (
        <Link className='text-decoration-none' to='/admin/dashboard'>
          Dashboard
        </Link>
      ),
    },
    {
      key: '/admin/users',
      icon: <UsergroupAddOutlined />,
      label: (
        <Link className='text-decoration-none' to='/admin/users'>
          Users
        </Link>
      ),
    },
    {
      key: '/admin/permissions',
      icon: <ProductOutlined />,
      label: (
        <Link className='text-decoration-none' to='/admin/permissions'>
          Permissions
        </Link>
      ),
    },
    // Show only "All Leads" menu item - other options moved to Exchange Stages dropdown filter
    {
      key: getLeadsBaseUrl(),
      icon: <SolutionOutlined />,
      label: (
        <Link className='text-decoration-none' to={getLeadsBaseUrl()}>
          Leads
        </Link>
      ),
    },
    hasStockPermission && {
      key: '/admin/stock',
      icon: <ProductOutlined />,
      label: (
        <Link className='text-decoration-none' to='/admin/stock'>
          Stock
        </Link>
      ),
    },
    hasRequestPermission && {
      key: '/admin/requests',
      icon: <SolutionOutlined />,
      label: 'Requests',
      children: [
        {
          key: '/admin/requests/allocation',
          label: (
            <Link className='text-decoration-none' to='/admin/requests/allocation'>
              Allocation Requests
            </Link>
          ),
        },
        {
          key: '/admin/requests/delivery',
          label: (
            <Link className='text-decoration-none' to='/admin/requests/delivery'>
              Delivery Requests
            </Link>
          ),
        },
        {
          key: '/admin/requests/cancellation',
          label: (
            <Link className='text-decoration-none' to='/admin/requests/cancellation'>
              Cancellation Requests
            </Link>
          ),
        },
        canSeePendingRequests && {
          key: '/admin/requests/pending',
          label: (
            <Link className='text-decoration-none' to='/admin/requests/pending'>
              Pending Requests
            </Link>
          ),
        },
      ].filter(Boolean),
    },
    {
      key: '/admin/add-target',
      icon: <ProductOutlined />,
      label: (
        <Link className='text-decoration-none' to='/admin/add-target'>
          Targets
        </Link>
      ),
    },
    {
      key: '/admin/masters',
      icon: <UserOutlined />,
      label: 'Masters',
      children: [
        {
          key: '/admin/masters/target',
          icon: <AimOutlined />,
          label: (
            <Link className='text-decoration-none' to='/admin/masters/target'>
              Target
            </Link>
          ),
        },
        {
          key: '/admin/masters/model',
          icon: <CodepenOutlined />,
          label: (
            <Link className='text-decoration-none' to='/admin/masters/model'>
              Model
            </Link>
          ),
        },
        {
          key: '/admin/masters/variant',
          icon: <ClusterOutlined />,
          label: (
            <Link className='text-decoration-none' to='/admin/masters/variant'>
              Variant
            </Link>
          ),
        },
        {
          key: '/admin/masters/exchange-model',
          icon: <InteractionOutlined />,
          label: (
            <Link className='text-decoration-none' to='/admin/masters/exchange-model'>
              Exchange Vehicle
            </Link>
          ),
        },
        {
          key: '/admin/masters/vehicle',
          icon: <CarOutlined />,
          label: (
            <Link className='text-decoration-none' to='/admin/masters/vehicle'>
              Vehicle
            </Link>
          ),
        },

        {
          key: '/admin/masters/transmission',
          icon: <DashboardOutlined />,
          label: (
            <Link className='text-decoration-none' to='/admin/masters/transmission'>
              Transmission
            </Link>
          ),
        },
        {
          key: '/admin/masters/territory',
          icon: <EnvironmentOutlined />,
          label: (
            <Link className='text-decoration-none' to='/admin/masters/territory'>
              Territory
            </Link>
          ),
        },
        {
          key: '/admin/masters/area',
          icon: <BorderOuterOutlined />,
          label: (
            <Link className='text-decoration-none' to='/admin/masters/area'>
              Area
            </Link>
          ),
        },
        {
          key: '/admin/masters/bank',
          icon: <BankOutlined />,
          label: (
            <Link className='text-decoration-none' to='/admin/masters/bank'>
              Bank
            </Link>
          ),
        },
      ],
    },
    {
      key: '/admin/reports',
      icon: <FileTextOutlined />,
      label: (
        <Link className='text-decoration-none' to='/admin/reports'>
          Reports
        </Link>
      ),
    },
  ];

  const usersSideBarOptions = [
    {
      key: 'dashboard',
      icon: <PieChartOutlined />,
      label: (
        <Link className='text-decoration-none' to='/dashboard'>
          Dashboard
        </Link>
      ),
    },
    hasUserPermission && {
      key: 'users',
      icon: <UsergroupAddOutlined />,
      label: (
        <Link className='text-decoration-none' to='/users'>
          Users
        </Link>
      ),
    },
    // Show only "All Leads" menu item - other options moved to Exchange Stages dropdown filter
    // Exchange Evaluation Team and Procurement Manager should always see Leads menu
    shouldShowLeadsMenu && {
      key: getLeadsBaseUrl(),
      icon: <SolutionOutlined />,
      label: (
        <Link className='text-decoration-none' to={getLeadsBaseUrl()}>
          Leads
        </Link>
      ),
    },
    hasTargetPermission && {
      key: 'add-target',
      icon: <ProductOutlined />,
      label: (
        <Link className='text-decoration-none' to='/add-target'>
          Target
        </Link>
      ),
    },
    hasStockPermission && {
      key: '/admin/stock',
      icon: <ProductOutlined />,
      label: (
        <Link className='text-decoration-none' to='/admin/stock'>
          Stock
        </Link>
      ),
    },
    hasRequestPermission && {
      key: '/admin/requests',
      icon: <SolutionOutlined />,
      label: 'Requests',
      children: [
        {
          key: '/admin/requests/allocation',
          label: (
            <Link className='text-decoration-none' to='/admin/requests/allocation'>
              Allocation Requests
            </Link>
          ),
        },
        {
          key: '/admin/requests/delivery',
          label: (
            <Link className='text-decoration-none' to='/admin/requests/delivery'>
              Delivery Requests
            </Link>
          ),
        },
        {
          key: '/admin/requests/cancellation',
          label: (
            <Link className='text-decoration-none' to='/admin/requests/cancellation'>
              Cancellation Requests
            </Link>
          ),
        },
        canSeePendingRequests && {
          key: '/admin/requests/pending',
          label: (
            <Link className='text-decoration-none' to='/admin/requests/pending'>
              Pending Requests
            </Link>
          ),
        },
      ].filter(Boolean),
    },
  ];

  // Determine which sidebar menu to use
  let sideBarMenu = isAdmin ? adminSideBarOptions : usersSideBarOptions;

  // Filter out falsy values
  sideBarMenu = sideBarMenu.filter(Boolean);



  return (
    <Layout>
      <PageHeader
        userType={roleType}
        collapsed={collapsed}
        mobileView={isMobileView}
        handleMobileMenuToggle={() => {
          setIsMobileView(!isMobileView);
        }}
        toggleCollapsed={() => {
          setCollapsed(!collapsed);
        }}
        pageHeaderClick={() => handleMainContentClick()}
      />
      <Layout className='layout-with-sidebar'>
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          onCollapse={() => setCollapsed(!collapsed)}
          width={225}
          style={{
            background: colorBgContainer,
          }}
          className={`custom-sidebar ${isMobileView ? 'hide' : 'show'}`}
        >
          <Menu
            mode='inline'
            style={{
              height: '100%',
              borderRight: 0,
            }}
            selectedKeys={selectedKeys}
            openKeys={openKeys}
            onOpenChange={onOpenChange}
            items={sideBarMenu}
          />
        </Sider>
        <Content style={contentStyle} onClick={() => handleMainContentClick()}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
};

export default DefaultLayout;
